<template>
  <FormCard :showHeader="false">
    <div class="text-center">
      <!-- Success Animation -->
      <div class="success-animation mb-4">
        <div class="success-circle">
          <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>
      
      <h2 class="text-3xl sm:text-4xl font-bold mb-2 bg-gradient-to-r from-green-500 to-emerald-600 bg-clip-text text-transparent">
        {{ $t('successPage.title') }}
      </h2>
      <p class="text-xl mb-4 text-gray-700">{{ $t('successPage.subtitle') }}</p>
      <p class="text-gray-600 mb-6 max-w-lg mx-auto">
        {{ $t('successPage.description') }}
      </p>

      <!-- Download Section -->
      <div v-if="showDownloadSection" class="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 mb-6 border border-green-200">
        <div class="flex items-center justify-center gap-3 mb-4">
          <div class="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">{{ $t('successPage.downloadTitle') }}</h3>
            <p class="text-sm text-gray-600 mt-1">{{ $t('successPage.downloadHint') }}</p>
          </div>
        </div>
        
        <div class="flex justify-center">
          <button 
            @click="$emit('download-pdf')"
            class="btn-primary"
            :disabled="downloadingPDF"
          >
            <svg v-if="downloadingPDF" class="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <svg v-else class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {{ downloadingPDF ? $t('successPage.downloadingBtn') : $t('successPage.downloadBtn') }}
          </button>
        </div>
      </div>

      <!-- Next Steps -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 mb-6 border border-blue-200">
        <div class="flex items-center justify-center gap-3 mb-3">
          <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900">{{ $t('successPage.nextStepsTitle') }}</h3>
        </div>
        <ul class="text-left space-y-2 text-gray-600 max-w-md mx-auto">
          <li class="flex items-start gap-2">
            <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span>{{ $t('successPage.nextStep1') }}</span>
          </li>
          <li class="flex items-start gap-2">
            <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span>{{ $t('successPage.nextStep2') }}</span>
          </li>
          <li class="flex items-start gap-2">
            <svg class="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span>{{ $t('successPage.nextStep3') }}</span>
          </li>
        </ul>
      </div>
      
      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <a href="/" class="btn-success group">
          <svg class="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          {{ $t('successPage.homeBtn') }}
        </a>
        <button @click="$emit('reset')" class="btn-secondary group">
          <svg class="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {{ $t('successPage.resetBtn') }}
        </button>
      </div>
    </div>
  </FormCard>
</template>

<script>
import FormCard from '../ui/FormCard.vue';

export default {
  name: 'SuccessPage',
  components: { FormCard },
  props: {
    showDownloadSection: { type: Boolean, default: false },
    downloadingPDF: { type: Boolean, default: false }
  },
  emits: ['download-pdf', 'reset']
}
</script>

<style scoped>
.success-animation {
  @apply inline-block;
  animation: scaleIn 0.5s ease-out;
}

.success-circle {
  @apply w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full
         flex items-center justify-center mx-auto shadow-2xl;
  animation: pulse-light 2s infinite;
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes pulse-light {
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
  50% { box-shadow: 0 0 0 20px rgba(34, 197, 94, 0); }
}

.btn-primary {
  @apply bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700
         text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg
         transition-all duration-300 inline-flex items-center justify-center disabled:opacity-50
         disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98];
}

.btn-secondary {
  @apply bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-5 rounded-xl
         shadow-md hover:shadow-lg transition-all duration-300 inline-flex items-center justify-center
         border border-gray-300 transform hover:scale-[1.02] active:scale-[0.98];
}

.btn-success {
  @apply bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700
         text-white font-semibold py-2.5 px-5 rounded-xl shadow-md hover:shadow-lg
         transition-all duration-300 inline-flex items-center justify-center
         transform hover:scale-[1.02] active:scale-[0.98];
}
</style>